/* eslint-disable max-lines */
/**
 * Design Lab Init Hook
 * React integration for the simplified init machine
 */

import { useMachine } from "@xstate/react";
// @ts-ignore - custom apollo wrapper
import { useApolloClient } from "@design-lab/shared/graphql";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import {
  actions,
  designLabInitMachine,
  guards,
  services,
  type DesignLabInitContext
} from "../machines/design-lab-init";

// Import existing edit mode hook (adjust path as needed)
// import { useEditMode } from '../useEditMode';

export interface UseDesignLabInitReturn {
  // State flags
  isEditMode: boolean;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;

  // Machine state
  state: string;
  isWaitingForLibrary: boolean;
  isWaitingForCanva: boolean;

  // Flow type
  flowType: DesignLabInitContext["flowType"];

  // Project data
  project: DesignLabInitContext["project"];
  latestProject: DesignLabInitContext["latestProject"];
  composition: DesignLabInitContext["composition"];

  // Upload state
  uploadSource: DesignLabInitContext["uploadSource"];
  uploadedFiles: DesignLabInitContext["uploadedFiles"];
  externalUploads: DesignLabInitContext["externalUploads"];
  targetSide: "front" | "back";

  // UI State
  showModal: boolean;
  modalAction: DesignLabInitContext["modalAction"];

  // Error
  error: DesignLabInitContext["error"];

  // Actions
  continueWithLatest: () => void;
  startNew: (source: "device" | "library" | "canva") => void;
  closeModal: () => void;
  selectFiles: (files: File[]) => void;
  handleLibrarySelection: (artworkId?: number | null) => void;
  handleCanvaComplete: (data: any) => void;
  cancel: () => void;
  retry: () => void;
  reset: () => void;
}

export function useDesignLabInit(): UseDesignLabInitReturn {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  // Initialize machine with Apollo client
  const [state, send] = useMachine(
    designLabInitMachine.provide({
      actions: actions as any,
      guards: guards as any,
      actors: services as any
    }),
    {
      input: {
        apolloClient,
        navigate
      }
    }
  );

  const { context } = state;

  // Check for edit mode
  // Note: If edit mode is detected, the machine stops at 'editModeDetected'
  // The existing useEditMode hook should be used in the parent component
  const isEditMode = context.isEditMode || state.matches("editModeDetected");

  // State flags
  const isReady = state.matches("ready");
  // We're loading if we're actively fetching data, not when waiting for user input
  const isLoading =
    state.matches("loadingProject") ||
    state.matches("creatingProject") ||
    state.matches("checkingLatestProject") ||
    state.matches("restoringProject") ||
    state.matches("processingUpload");
  const isError = state.matches("error");

  // Actions
  const continueWithLatest = useCallback(() => {
    send({ type: "CONTINUE_WITH_LATEST" });
  }, [send]);

  const startNew = useCallback(
    (source: "device" | "library" | "canva") => {
      send({ type: "START_NEW", source });
    },
    [send]
  );

  const closeModal = useCallback(() => {
    send({ type: "CLOSE_MODAL" });
  }, [send]);

  const selectFiles = useCallback(
    (files: File[]) => {
      send({ type: "FILES_SELECTED", files });
    },
    [send]
  );

  const handleLibrarySelection = useCallback(
    (artworkId?: number | null) => {
      send({ type: "LIBRARY_SELECTION", artworkId: artworkId || 0 });
    },
    [send]
  );

  const handleCanvaComplete = useCallback(
    (data: any) => {
      send({ type: "CANVA_COMPLETE", data });
    },
    [send]
  );

  const cancel = useCallback(() => {
    send({ type: "CANCEL" });
  }, [send]);

  const retry = useCallback(() => {
    send({ type: "RETRY" });
  }, [send]);

  const reset = useCallback(() => {
    send({ type: "RESET" });
  }, [send]);

  return {
    // State flags
    isEditMode,
    isReady,
    isLoading,
    isError,

    // Machine state - expose for UI to check specific states
    state: state.value as string,
    isWaitingForLibrary: state.matches("processingUpload.openingLibrary"),
    isWaitingForCanva: state.matches("processingUpload.openingCanva"),

    // Flow type
    flowType: context.flowType,

    // Project data
    project: context.project,
    latestProject: context.latestProject,
    composition: context.composition,

    // Upload state
    uploadSource: context.uploadSource,
    uploadedFiles: context.uploadedFiles,
    externalUploads: context.externalUploads,
    targetSide: context.targetSide,

    // UI State
    showModal: context.showModal,
    modalAction: context.modalAction,

    // Error
    error: context.error,

    // Actions
    continueWithLatest,
    startNew,
    closeModal,
    selectFiles,
    handleLibrarySelection,
    handleCanvaComplete,
    cancel,
    retry,
    reset
  };
}
